import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

// Mirrors the breakpoint names defined as `--breakpoint-*` custom properties
// in @payaca/ambrosia/tailwind.config.css. Add new entries there if you need
// them here.
export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type Container = 'document' | 'main';

const MAIN_SELECTOR = 'main#main-page-content';

export const getBreakpointPx = (breakpoint: Breakpoint): number | null => {
  if (typeof window === 'undefined') return null;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--breakpoint-${breakpoint}`)
    .trim();
  const px = parseFloat(value);
  if (!Number.isFinite(px))
    throw new Error(`Invalid breakpoint value for ${breakpoint}: "${value}"`);
  return px;
};

// Returns true once the container is at least as wide as the named breakpoint
// — i.e. the same threshold Tailwind uses for `md:`, `lg:`, etc.
const useIsContainerBeyondBreakpoint = (
  breakpoint: Breakpoint,
  options: { container?: Container } = {}
): boolean => {
  const { container = 'main' } = options;
  const px = useMemo(() => getBreakpointPx(breakpoint), [breakpoint]);

  // Resolve synchronously on first render. For 'document' we use matchMedia;
  // for 'main' we measure the element directly — this works as long as the
  // hook is called within AuthenticatedPageWrapper, which renders
  // main#main-page-content as an ancestor.
  const [matches, setMatches] = useState(() => {
    if (px === null) return false;
    if (container === 'document') {
      return window.matchMedia(`(width >= ${px}px)`).matches;
    }
    const main = document.querySelector(MAIN_SELECTOR);
    if (!main) return false;
    return main.getBoundingClientRect().width >= px;
  });

  useEffect(() => {
    if (px === null || container !== 'document') return undefined;
    const mql = window.matchMedia(`(width >= ${px}px)`);
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [container, px]);

  useLayoutEffect(() => {
    if (px === null || container !== 'main') return undefined;
    const main = document.querySelector(MAIN_SELECTOR);
    if (!main) return undefined;
    setMatches(main.getBoundingClientRect().width >= px);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setMatches(entry.contentRect.width >= px);
    });
    observer.observe(main);
    return () => observer.disconnect();
  }, [container, px]);

  return matches;
};

export default useIsContainerBeyondBreakpoint;
